import { collection, doc, getDoc, getDocs, getFirestore, limit, orderBy, query, where } from 'firebase/firestore';

// My Card（学生向けアプリのプロフィールカード）をサロン側から読み取るための
// データ取得ロジック。実体は users/{uid} ドキュメント内のフィールド群であり、
// 新規コレクションではない（成長ログ users/{uid}/growthLogs のみサブコレクション）。
// フィールド仕様・フォールバック規則は memorii(Flutter) lib/9.type/account.dart の
// UserAccount.fromMap をWEB側に移植したもの。

export type AboutMeKey =
  | 'idealBeautician' | 'reasonForCareer' | 'customerServiceValue'
  | 'favoriteStyle' | 'currentEffort' | 'futureGoal' | 'salonPreference';

export const ABOUT_ME_ORDER: { key: AboutMeKey; label: string }[] = [
  { key: 'idealBeautician', label: '理想の美容師像' },
  { key: 'reasonForCareer', label: 'この道を志した理由' },
  { key: 'customerServiceValue', label: '接客で大切にしていること' },
  { key: 'favoriteStyle', label: '好きなスタイル' },
  { key: 'currentEffort', label: '今頑張っていること' },
  { key: 'futureGoal', label: '将来の目標' },
  { key: 'salonPreference', label: '求めるサロン像' },
];

// アプリ内ハードコードのMy Styleタグ(Firestoreにマスタなし)。lib/9.type/style_tag.dart のコピー。
export const STYLE_TAG_LABELS: Record<string, string> = {
  natural: 'ナチュラル',
  korean: '韓国ヘア',
  mode: 'モード',
  girly: 'ガーリー',
  mens: 'メンズスタイル',
  bob: 'ボブ',
  high_tone: 'ハイトーン',
  warm_color: '暖色カラー',
  clear_color: '透明感カラー',
  hair_arrange: 'ヘアアレンジ',
};

export const GROWTH_LOG_CATEGORY_LABELS: Record<string, string> = {
  practice: '練習',
  model: 'モデル施術',
  certification: '検定',
  contest: 'コンテスト',
  school: '学校行事',
  seminar: 'セミナー',
  job_hunting: '就職活動',
  other: 'その他',
};

export type SalonProfileData = {
  preferredAreas: string[];
  desiredJobType: string;
  desiredStartDate: string;
  questionsForSalon: string[];
  messageToSalon: string;
  contactMethod: string;
};

export type StudentProfile = {
  uid: string;
  displayName: string; // profile.name（ユーザーネーム。本名ではない）
  realName: string; // education.name（本名）
  bio: string; // profile.text
  business: string;
  sns: { instagram: string; tiktok: string };
  education: { school: string; department: string; grade: string; class: string; number: string };
  myCard: {
    styleTagIds: string[];
    pickupWorkIds: string[];
    aboutMe: Record<AboutMeKey, string>;
    graduationYear: number | null;
    graduationMonth: number | null;
    salonProfile: SalonProfileData;
  };
};

export type GrowthLogEntry = {
  id: string;
  title: string;
  category: string;
  activityDate: Date;
  description: string;
  imageUrl: string | null;
};

export type PickupWork = {
  id: string;
  caption: string;
};

function pick<T>(myCardValue: T | undefined | null, legacyValue: T | undefined | null, fallback: T): T {
  if (myCardValue !== undefined && myCardValue !== null) return myCardValue;
  if (legacyValue !== undefined && legacyValue !== null) return legacyValue;
  return fallback;
}

export async function fetchStudentProfile(uid: string): Promise<StudentProfile | null> {
  if (!uid) return null;
  const db = getFirestore();
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  const data = snap.data();

  const profile = data.profile ?? {};
  const myCard = profile.myCard ?? {};
  const education = data.education ?? {};

  const aboutMe: Record<AboutMeKey, string> = {} as Record<AboutMeKey, string>;
  ABOUT_ME_ORDER.forEach(({ key }) => {
    aboutMe[key] = pick(myCard.aboutMe?.[key], profile.aboutMe?.[key], '');
  });

  return {
    uid,
    displayName: profile.name ?? '',
    realName: education.name ?? '',
    bio: profile.text ?? '',
    business: profile.business ?? '美容学生',
    sns: {
      instagram: profile.sns?.instagram ?? '',
      tiktok: profile.sns?.tiktok ?? '',
    },
    education: {
      school: education.school ?? '',
      department: education.department ?? '',
      grade: education.grade ?? '',
      class: education.class ?? '',
      number: education.number ?? '',
    },
    myCard: {
      styleTagIds: pick(myCard.styleTagIds, profile.styleTagIds, []),
      pickupWorkIds: pick(myCard.pickupWorkIds, profile.pickupWorkIds, []),
      aboutMe,
      graduationYear: pick(myCard.graduation?.year, profile.graduation?.year, null),
      graduationMonth: pick(myCard.graduation?.month, profile.graduation?.month, null),
      salonProfile: {
        preferredAreas: pick(myCard.salonProfile?.preferredAreas, profile.salonProfile?.preferredAreas, []),
        desiredJobType: pick(myCard.salonProfile?.desiredJobType, profile.salonProfile?.desiredJobType, ''),
        desiredStartDate: pick(myCard.salonProfile?.desiredStartDate, profile.salonProfile?.desiredStartDate, ''),
        questionsForSalon: pick(myCard.salonProfile?.questionsForSalon, profile.salonProfile?.questionsForSalon, []),
        messageToSalon: pick(myCard.salonProfile?.messageToSalon, profile.salonProfile?.messageToSalon, ''),
        contactMethod: pick(myCard.salonProfile?.contactMethod, profile.salonProfile?.contactMethod, ''),
      },
    },
  };
}

function toDate(value: any): Date {
  if (value?.toDate) return value.toDate();
  if (value) return new Date(value);
  return new Date();
}

// サロン閲覧ビューでは visibility: public / salon_only の両方を対象にする
// (public のみだと salon_only が漏れる。lib/9.type/growth_log.dart 参照)。
export async function fetchGrowthLogs(uid: string): Promise<GrowthLogEntry[]> {
  if (!uid) return [];
  const db = getFirestore();
  const q = query(
    collection(db, 'users', uid, 'growthLogs'),
    where('visibility', 'in', ['public', 'salon_only']),
    orderBy('activityDate', 'desc'),
    limit(3),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      title: data.title ?? '',
      category: data.category ?? 'practice',
      activityDate: toDate(data.activityDate),
      description: data.description ?? '',
      imageUrl: Array.isArray(data.imageUrls) && data.imageUrls.length > 0 ? data.imageUrls[0] : null,
    };
  });
}

export async function fetchPickupWorks(postIds: string[]): Promise<PickupWork[]> {
  const db = getFirestore();
  const results = await Promise.all(postIds.slice(0, 3).map(async (id) => {
    const snap = await getDoc(doc(db, 'posts', id));
    if (!snap.exists()) return null;
    const tags = snap.data()?.post_tags;
    return { id, caption: Array.isArray(tags) && tags.length > 0 ? tags[0] : '' } as PickupWork;
  }));
  return results.filter((r): r is PickupWork => r !== null);
}

export function profileImageUrl(uid: string): string {
  return `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/profiles%2Fresize_images%2F${uid}_500x500?alt=media`;
}

export function pickupWorkImageUrl(postId: string): string {
  return `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/image%2Fresize_images%2F${postId}_500x500?alt=media`;
}
